import CoreUtils from '../utils/CoreUtils';
import ArrayUtils from '../utils/ArrayUtils';

export default class EnergyValueUtils {
  static isValidResolution(resolution) {
    switch (resolution) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 12:
        return true;
      default:
        return false;
    }
  }

  static isCompatibleResolution(originalResolution, targetResolution) {
    if (!EnergyValueUtils.isValidResolution(targetResolution))
      return false;

    return targetResolution >= originalResolution
      && (targetResolution % originalResolution) === 0;
  }

  static findCommonResolution(resolutionArg1, resolutionArg2) {
    if (EnergyValueUtils.isValidValue(resolutionArg1) && EnergyValueUtils.isValidValue(resolutionArg2)) {
      if (resolutionArg1 === resolutionArg2)
        return resolutionArg1;

      var higherResolution = Math.max(resolutionArg1, resolutionArg2);
      var lowerResolution = Math.min(resolutionArg1, resolutionArg2);

      return higherResolution % lowerResolution === 0
        ? higherResolution
        : higherResolution * lowerResolution;
    }

    if (!EnergyValueUtils.isValidValueArray(resolutionArg1))
      return 0;

    var resolution = resolutionArg1[0];
    for (var index = 1; index < resolutionArg1.length; index++)
      resolution = EnergyValueUtils.findCommonResolution(resolutionArg1[index], resolution);

    return resolution;
  }

  static isValidValue(value) {
    return typeof value === 'number' && !isNaN(value) && value % 1 === 0;
  }

  static isValidValueArray(values) {
    if (CoreUtils.isUndefinedOrNull(values) || !Array.isArray(values))
      return false;

    if (values.length === 0)
      return false;

    for (var index = 0; index < values.length; index++) {
      if (!EnergyValueUtils.isValidValue(values[index]))
        return false;
    }

    return true;
  }

  static areValidValueVectors(valueVectors) {
    if (CoreUtils.isUndefinedOrNull(valueVectors) || !Array.isArray(valueVectors))
      return false;

    if (valueVectors.length === 0)
      return false;

    for (var index = 0; index < valueVectors.length; index++) {
      if (!EnergyValueUtils.isValidValueArray(valueVectors[index]))
        return false;
    }

    return true;
  }

  static isValidArray(values) {
    if (CoreUtils.isUndefinedOrNull(values) || !Array.isArray(values))
      return false;

    return values.length > 0;
  }

  static isValidMaskValue(maskValue) {
    return typeof maskValue === 'boolean';
  }

  static isValidMaskValueArray(maskValues) {
    if (CoreUtils.isUndefinedOrNull(maskValues) || !Array.isArray(maskValues))
      return false;

    if (maskValues.length === 0)
      return false;

    for (var index = 0; index < maskValues.length; index++) {
      if (!EnergyValueUtils.isValidMaskValue(maskValues[index]))
        return false;
    }

    return true;
  }

  static intersect(values1, values2, intersectFunc, maximumResolution) {
    if (!EnergyValueUtils.isValidArray(values1)
      || !EnergyValueUtils.isValidArray(values2)
      || typeof intersectFunc !== 'function'
      || !EnergyValueUtils.isValidResolution(maximumResolution))
      return [0];

    var commonResolution = EnergyValueUtils.findCommonResolution(
      values1.length,
      values2.length
    );

    var intersectValues = ArrayUtils.intersectArrays(
      EnergyValueUtils.upscaleResolution(values1, commonResolution),
      EnergyValueUtils.upscaleResolution(values2, commonResolution),
      intersectFunc
    );

    return EnergyValueUtils.downscaleResolution
    (
      intersectValues,
      commonResolution <= maximumResolution
        ? commonResolution
        : maximumResolution,
      intersectFunc
    );
  }

  static operateOnMultipleValues(valueVectors, operation, targetResolution) {
    if (typeof operation !== 'function'
      || !EnergyValueUtils.areValidValueVectors(valueVectors)
      || !EnergyValueUtils.isValidResolution(targetResolution))
      return undefined;

    if (valueVectors.length === 1)
      return valueVectors[0];

    var commonResolution = EnergyValueUtils.findCommonResolution(
      valueVectors.map(values => values.length)
    );

    var upscaledVectors = valueVectors.map(
      values => EnergyValueUtils.upscaleResolution(values, commonResolution)
    );

    var results = [];
    for (var operandIndex = 0; operandIndex < commonResolution; operandIndex++) {
      var operands = ArrayUtils.mapWithParameters(
        upscaledVectors,
        (values, valueIndex) => values[valueIndex],
        operandIndex
      );

      results.push(operation(operands));
    }

    return EnergyValueUtils.rescale(
      results,
      EnergyValueUtils.averageValue,
      targetResolution
    );
  }

  static rescale(values, downscaleFunc, targetResolution) {
    if (!EnergyValueUtils.isValidArray(values)
      || !EnergyValueUtils.isValidResolution(targetResolution)
      || typeof downscaleFunc !== 'function')
      return values;

    if (values.length === targetResolution)
      return values;

    return values.length > targetResolution
      ? EnergyValueUtils.downscaleResolution
        (
          values,
          targetResolution,
          downscaleFunc
        )
      : EnergyValueUtils.upscaleResolution
        (
          values,
          targetResolution
        );
  }

  static overwriteValues(originalValues, overwriteValues, overwriteMask, downscaleFunc, maximumResolution) {
    if (!EnergyValueUtils.isValidArray(originalValues)
      || !EnergyValueUtils.isValidArray(overwriteValues)
      || !EnergyValueUtils.isValidMaskValueArray(overwriteMask)
      || typeof downscaleFunc !== 'function'
      || !EnergyValueUtils.isValidResolution(maximumResolution))
      return originalValues;

    var commonResolution = EnergyValueUtils.findCommonResolution(
      originalValues.length,
      overwriteValues.length
    );

    commonResolution = EnergyValueUtils.findCommonResolution(
      commonResolution,
      overwriteMask.length
    );

    var upscaledOriginalValues = Array.from(EnergyValueUtils.upscaleResolution(originalValues, commonResolution));
    var upscaledOverwriteValues = EnergyValueUtils.upscaleResolution(overwriteValues, commonResolution);
    var upscaledOverwriteMask = EnergyValueUtils.upscaleResolution(overwriteMask, commonResolution);

    for (var index = 0; index < commonResolution; index++) {
      if (upscaledOverwriteMask[index])
        upscaledOriginalValues[index] = upscaledOverwriteValues[index];
    }

    return EnergyValueUtils.downscaleResolution(
      upscaledOriginalValues,
      commonResolution <= maximumResolution
        ? commonResolution
        : maximumResolution,
      downscaleFunc
    );
  }

  static getEqualityMask(values1, values2) {
    if (!EnergyValueUtils.isValidArray(values1)
      || !EnergyValueUtils.isValidArray(values2))
      return [false];

    var commonResolution = EnergyValueUtils.findCommonResolution(
      values1.length,
      values2.length
    );

    var upscaledValues1 = EnergyValueUtils.upscaleResolution(values1, commonResolution);
    var upscaledValues2 = EnergyValueUtils.upscaleResolution(values2, commonResolution);

    var equalityMask = [];
    for (var index = 0; index < commonResolution; index++)
      equalityMask[index] = upscaledValues1[index] === upscaledValues2[index];

    return equalityMask;
  }

  static areValuesEqual(values1, values2, equalityMask) {
    if (!EnergyValueUtils.isValidArray(values1)
      || !EnergyValueUtils.isValidArray(values2)
      || !EnergyValueUtils.isValidMaskValueArray(equalityMask))
      return false;

    var commonResolution = EnergyValueUtils.findCommonResolution(
      values1.length,
      values2.length
    );

    commonResolution = EnergyValueUtils.findCommonResolution(
      commonResolution,
      equalityMask.length
    );

    var upscaledValues1 = EnergyValueUtils.upscaleResolution(values1, commonResolution);
    var upscaledValues2 = EnergyValueUtils.upscaleResolution(values2, commonResolution);
    var upscaledEqualityMask = EnergyValueUtils.upscaleResolution(equalityMask, commonResolution);

    for (var index = 0; index < commonResolution; index++) {
      if (upscaledEqualityMask[index] && upscaledValues1[index] !== upscaledValues2[index])
        return false;
    }

    return true;
  }

  static upscaleResolution(values, targetResolution) {
    if (values.length >= targetResolution)
      return values;

    var upscaleFactor = Math.floor(targetResolution / values.length);
    var targetValues = [];

    for (var index = 0; index < targetResolution; index++)
      targetValues[index] = values[Math.floor(index / upscaleFactor)];

    return targetValues;
  }

  static downscaleResolution(values, targetResolution, downscaleFunc) {
    if (values.length <= targetResolution)
      return values;

    var downscaleFactor = Math.floor(values.length / targetResolution);
    var targetValues = [];

    for (var index = 0; index < targetResolution; index++) {
      var subArray = values.slice(
        index * downscaleFactor,
        (index + 1) * downscaleFactor
      );

      targetValues[index] = downscaleFunc(subArray);
    }

    return targetValues;
  }

  static minimumValue(values) {
    if (!EnergyValueUtils.isValidValueArray(values) || values.length === 0)
      return 0;

    var minValue = values[0];
    for (var index = 1; index < values.length; index++)
      minValue = Math.min(minValue, values[index]);

    return minValue;
  }

  static maximumValue(values) {
    if (!EnergyValueUtils.isValidValueArray(values) || values.length === 0)
      return 0;

    var maxValue = values[0];
    for (var index = 1; index < values.length; index++)
      maxValue = Math.max(maxValue, values[index]);

    return maxValue;
  }

  static summationValue(values) {
    if (!EnergyValueUtils.isValidValueArray(values) || values.length === 0)
      return 0;

    var sumValue = 0;
    for (var index = 0; index < values.length; index++)
      sumValue += values[index];

    return sumValue;
  }

  static averageValue(values) {
    if (!EnergyValueUtils.isValidValueArray(values) || values.length === 0)
      return 0;

    var sumValue = 0;
    for (var index = 0; index < values.length; index++)
      sumValue += values[index];

    return Math.round(sumValue / values.length);
  }

  static normalizeValues(values) {
    if (CoreUtils.isUndefinedOrNull(values) || !Array.isArray(values))
      return values;

    var valueGroups = [];
    var valueGroup = null;

    for (var index = 0; index < values.length; index++) {
      if (valueGroup !== null && valueGroup.value !== values[index])
        valueGroup = null;

      if (valueGroup === null) {
        valueGroup = {
          value: values[index],
          count: 0
        };

        valueGroups.push(valueGroup);
      }

      valueGroup.count++;
    }

    return EnergyValueUtils.areAllGroupCountsEqual(valueGroups)
      ? valueGroups.map(valueGroup => valueGroup.value)
      : Array.from(values);
  }

  static areAllGroupCountsEqual(valueGroups) {
    var previousCount = null;

    for (var index = 0; index < valueGroups.length; index++) {
      var valueGroup = valueGroups[index];
      if (previousCount === null)
        previousCount = valueGroup.count;
      else if (previousCount !== valueGroup.count)
        return false;
    }

    return true;
  }

  static calculateValueStatistics(values) {
    var sumValue = 0;
    var minimumValue = null;
    var maximumValue = null;
    var nonZeroCount = 0;

    for (var index = 0; index < values.length; index++) {
      var value = values[index];

      sumValue += value;

      if (minimumValue === null || value < minimumValue)
        minimumValue = value;

      if (maximumValue === null || value > maximumValue)
        maximumValue = value;

      if (value !== 0)
        nonZeroCount++;
    }

    return {
      averageValue: Math.round(sumValue / values.length),
      averageDuration: nonZeroCount / values.length,
      minimumValue: minimumValue,
      maximumValue: maximumValue
    };
  }
}